/**
 * Auth boundary — web mirror of the Swift app's `Sources/Auth/AuthBoundary.swift`.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │  AUTH MODEL — "Supabase Auth directly" (Path B).                          │
 * │                                                                            │
 * │  PRIMARY AUTH = Supabase Auth. The app signs in with @supabase/supabase-js │
 * │  `auth.signInWithPassword({ email, password })`, which returns a Supabase   │
 * │  session whose access_token is ES256-signed BY Supabase and carries a       │
 * │  `tenant_id` claim (stamped by a server-side Custom Access Token Hook). The  │
 * │  SDK persists (localStorage) + refreshes that session, and all factory_*     │
 * │  PostgREST reads + Realtime ride on it automatically. Postgres RLS reads     │
 * │  the `tenant_id` claim through `current_tenant_id()` and scopes every        │
 * │  factory_* row to the tenant — unchanged from before.                        │
 * │                                                                            │
 * │  WHY THE SWITCH: the Brain Supabase project signs JWTs with an ASYMMETRIC    │
 * │  ES256 key, so a Worker-minted HS256 token cannot be verified. Supabase       │
 * │  Auth mints valid ES256 tokens itself. The Worker `/supabase-token` mint      │
 * │  endpoint is now DEPRECATED for the app and is no longer called.             │
 * │                                                                            │
 * │  BRAIN TAB = `brain_` token (unchanged). The Brain tab's Worker surfaces      │
 * │  (memories / files / tmux / bridge-tokens) still authenticate with a          │
 * │  `brain_` bearer from the Brain Worker's own `/login`. `BrainTokenProvider`   │
 * │  owns that token (localStorage). Sign-in does a BEST-EFFORT Brain `/login`    │
 * │  in addition to the (required) Supabase sign-in; if it fails the Factory      │
 * │  side still works and the Brain tab shows a "connect Brain" state.            │
 * │                                                                            │
 * │  `StubAuth` remains for Mock (no network, no credentials).                    │
 * └──────────────────────────────────────────────────────────────────────────┘
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { TenantId } from '../models/factory';
import type { BrainConfig } from '../config';
import { brainConfigFromEnvironment } from '../config';

/** A resolved, signed-in tenant. `tenantId` is the value RLS matches against
 * `current_tenant_id()`; `accessToken` is the Supabase-Auth-issued ES256 JWT
 * (carrying the `tenant_id` claim). The SDK owns the durable session; this is a
 * lightweight snapshot the UI reads for display + gating. */
export interface AuthSession {
  tenantId: TenantId;
  displayName: string;
  accessToken: string | null;
}

/** The seam every auth model plugs into. Views/stores depend only on this. */
export interface AuthBoundary {
  currentSession(): AuthSession | null;
  /** Resume a persisted session (no interaction). StubAuth returns a fake tenant;
   * SupabaseAuth asks the SDK for its restored session. */
  signIn(): Promise<AuthSession>;
  signOut(): Promise<void>;
  /** The durable `brain_*` bearer token, if the Brain tab is connected. Unlike the
   * Supabase session this authenticates the Brain Worker's own routes (memories /
   * files / tmux / bridge-tokens). Null when the Brain tab is not connected. */
  currentBrainToken(): Promise<string | null>;
}

// MARK: - StubAuth (Mock default)

/** The shell's default: always "signed in" as a fake tenant so Mock data
 * renders standalone. No real credentials, no network, no auth decision. */
export class StubAuth implements AuthBoundary {
  /** A stable fake tenant id (uuid shape) used across MockFactoryStore rows. */
  static readonly fakeTenantId: TenantId = '00000000-0000-4000-8000-0000000f0001';

  private session: AuthSession | null;

  constructor(autoSignIn = true) {
    this.session = autoSignIn
      ? {
          tenantId: StubAuth.fakeTenantId,
          displayName: 'Sully (mock tenant)',
          accessToken: 'stub-token-not-a-real-jwt',
        }
      : null;
  }

  currentSession(): AuthSession | null {
    return this.session;
  }

  async signIn(): Promise<AuthSession> {
    const s: AuthSession = {
      tenantId: StubAuth.fakeTenantId,
      displayName: 'Sully (mock tenant)',
      accessToken: 'stub-token-not-a-real-jwt',
    };
    this.session = s;
    return s;
  }

  async signOut(): Promise<void> {
    this.session = null;
  }

  async currentBrainToken(): Promise<string | null> {
    // Mock has no real brain_ token; the Brain tab uses MockMemoriesStore.
    return null;
  }
}

// MARK: - Brain token storage (durable brain_ token for the Brain tab)

/** Minimal wrapper for the `brain_` bearer token used ONLY by the Brain tab's
 * Worker surfaces. Uses localStorage (browser Keychain analogue). The Supabase
 * session is persisted by supabase-js's own storage, not here. */
const BRAIN_TOKEN_KEY = 'com.sullyk.factory.web.brain-token';

const BrainTokenStore = {
  read(): string | null {
    try {
      return globalThis.localStorage?.getItem(BRAIN_TOKEN_KEY) ?? null;
    } catch {
      return null;
    }
  },
  save(value: string): void {
    try {
      globalThis.localStorage?.setItem(BRAIN_TOKEN_KEY, value);
    } catch {
      /* storage unavailable (private mode / SSR) — token stays in-memory only */
    }
  },
  delete(): void {
    try {
      globalThis.localStorage?.removeItem(BRAIN_TOKEN_KEY);
    } catch {
      /* ignore */
    }
  },
};

// MARK: - JWT (claims-only decode)

/** Tiny JWT payload decoder (claims only — no signature verification; Supabase
 * verifies the ES256 signature server-side). Used to read the `tenant_id` claim
 * off the Supabase-issued access token for display/gating. */
function decodeJwtClaims(token: string): Record<string, unknown> {
  const parts = token.split('.');
  if (parts.length !== 3) throw new BrainAuthError('notAJWT', 'access_token is not a JWT.');
  let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
  while (b64.length % 4 !== 0) b64 += '=';
  try {
    // Browser-native base64 decode. `atob` is always available in the browser
    // (this is a Vite SPA); the JWT claims here are ASCII, so a binary-string
    // decode is sufficient. Avoids Node's `Buffer`, which isn't in a browser.
    const json = atob(b64);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    throw new BrainAuthError('notAJWT', 'access_token JWT payload is not decodable.');
  }
}

// MARK: - Errors

type BrainAuthCode = 'needsLogin' | 'httpStatus' | 'badResponse' | 'notAJWT';

export class BrainAuthError extends Error {
  constructor(public code: BrainAuthCode, message: string) {
    super(message);
    this.name = 'BrainAuthError';
  }
}

type SupabaseAuthCode = 'noTenantClaim' | 'noSession' | 'signInFailed';

export class SupabaseAuthError extends Error {
  constructor(public code: SupabaseAuthCode, message: string) {
    super(message);
    this.name = 'SupabaseAuthError';
  }
}

// MARK: - BrainTokenProvider (Brain tab's brain_ bearer)

/** Owns the durable `brain_*` bearer token used ONLY by the Brain tab. Separate
 * from the Supabase session that powers the Factory/Chat/Notifications tabs. */
class BrainTokenProvider {
  private readonly config: BrainConfig;
  private brainToken: string | null;

  constructor(config: BrainConfig) {
    this.config = config;
    this.brainToken = BrainTokenStore.read();
  }

  currentBrainToken(): string | null {
    return this.brainToken;
  }

  get isConnected(): boolean {
    return this.brainToken !== null;
  }

  /** POST /login (email+password) → store the `brain_` token. Throws on failure;
   * sign-in treats a Brain-login failure as non-fatal. */
  async login(email: string, password: string): Promise<string> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const res = await fetch(new URL('/login', this.config.brainBaseURL).toString(), {
      method: 'POST',
      headers,
      body: JSON.stringify({ email, password }),
    });
    const text = await res.text();
    if (!res.ok) {
      throw new BrainAuthError('httpStatus', `Brain returned HTTP ${res.status}: ${text}`);
    }
    let payload: Record<string, unknown>;
    try {
      payload = JSON.parse(text) as Record<string, unknown>;
    } catch {
      throw new BrainAuthError('badResponse', 'Brain response was not a JSON object.');
    }
    const token = payload['token'];
    if (typeof token !== 'string' || token.length === 0) {
      throw new BrainAuthError('badResponse', 'Brain /login response missing token.');
    }
    this.brainToken = token;
    BrainTokenStore.save(token);
    return token;
  }

  clear(): void {
    this.brainToken = null;
    BrainTokenStore.delete();
  }
}

// MARK: - SupabaseAuth (real "Supabase Auth directly" implementation)

/** Primary auth. Owns the shared `SupabaseClient` (used by SupabaseFactoryStore
 * for reads + Realtime) and delegates the Brain tab's `brain_` token to
 * `BrainTokenProvider`. The SDK persists + refreshes the Supabase session, so
 * there is no manual mint/refresh loop and no `accessToken` closure. */
export class SupabaseAuth implements AuthBoundary {
  /** The shared client. `AppEnvironment.resolved()` hands the SAME client to
   * `SupabaseFactoryStore` — reads/Realtime ride on the signed-in session. */
  readonly client: SupabaseClient;

  private readonly brain: BrainTokenProvider;
  private cachedSession: AuthSession | null = null;

  constructor(config: BrainConfig = brainConfigFromEnvironment()) {
    // Default auth options: persistSession + autoRefreshToken to localStorage, so
    // the session survives reloads and is refreshed transparently before expiry.
    this.client = createClient(config.supabaseURL, config.supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
      },
    });
    this.brain = new BrainTokenProvider(config);
  }

  currentSession(): AuthSession | null {
    return this.cachedSession;
  }

  /** Resume a persisted Supabase session (SDK-restored). Throws `noSession` when
   * there is nothing to resume so `AppEnvironment` drops to the SignIn screen. */
  async signIn(): Promise<AuthSession> {
    const { data, error } = await this.client.auth.getSession();
    if (error) throw new SupabaseAuthError('noSession', error.message);
    const token = data.session?.access_token;
    if (!token) throw new SupabaseAuthError('noSession', 'No persisted Supabase session.');
    return this.makeSession(token);
  }

  /** Interactive sign-in. (1) Supabase Auth `signInWithPassword` (REQUIRED — gates
   * the app), then (2) a BEST-EFFORT Brain `/login` so the Brain tab is connected
   * too. A Brain-login failure is swallowed: the Factory side works regardless. */
  async login(email: string, password: string): Promise<AuthSession> {
    const { data, error } = await this.client.auth.signInWithPassword({ email, password });
    if (error) throw new SupabaseAuthError('signInFailed', error.message);
    const token = data.session?.access_token;
    if (!token) throw new SupabaseAuthError('signInFailed', 'Sign-in returned no session.');
    const session = this.makeSession(token);
    try {
      await this.brain.login(email, password);
    } catch {
      // Non-fatal: Factory/Chat/Notifications still work on the Supabase session;
      // the Brain tab shows a signed-out / "connect Brain" state.
    }
    return session;
  }

  async signOut(): Promise<void> {
    try {
      await this.client.auth.signOut();
    } catch {
      /* ignore — clear local state regardless */
    }
    this.brain.clear();
    this.cachedSession = null;
  }

  async currentBrainToken(): Promise<string | null> {
    return this.brain.currentBrainToken();
  }

  // MARK: - Internals

  private makeSession(jwt: string): AuthSession {
    const claims = decodeJwtClaims(jwt);
    const tenant = (claims['tenant_id'] ?? claims['sub']) as string | undefined;
    if (!tenant) {
      throw new SupabaseAuthError(
        'noTenantClaim',
        'Signed in, but the Supabase token has no tenant_id claim. ' +
          'Ensure the Custom Access Token Hook is enabled server-side.',
      );
    }
    const name = (claims['email'] as string | undefined) ?? 'Signed in';
    const session: AuthSession = { tenantId: tenant, displayName: name, accessToken: jwt };
    this.cachedSession = session;
    return session;
  }
}
